import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Torus } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface TorusMeshProperties extends TransformableItemProperties {
  radius: number;
  tubeRadius: number;
  radialSegments: number;
  tubularSegments: number;
  arc: number;
  materialId?: string;
}

export interface TorusMesh extends Mesh<TorusMeshProperties> {
  subtype: 'torus';
  properties: TorusMeshProperties;
}

// Create a reusable Torus component that can be used in both preview and world
const TorusComponent = forwardRef<THREE.Mesh, { properties: TorusMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radius, tubeRadius, radialSegments, tubularSegments, arc, transform, materialId } = properties;
  const { activeWorld } = useAppContext();

  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Torus
      ref={ref}
      args={[radius, tubeRadius, radialSegments, tubularSegments, arc]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const TorusMeshItem: TorusMesh = {
  id: '',
  name: 'Torus',
  type: 'mesh',
  subtype: 'torus',
  icon: 'Torus',
  properties: {
    radius: 1,
    tubeRadius: 0.4,
    radialSegments: 16,
    tubularSegments: 100,
    arc: Math.PI * 2,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.tubeRadius',
      type: 'number',
      label: 'Tube Radius',
      default: 0.4,
      panel: 'geometry'
    },
    {
      key: 'properties.radialSegments',
      type: 'number',
      label: 'Radial Segments',
      default: 16,
      panel: 'geometry'
    },
    {
      key: 'properties.tubularSegments',
      type: 'number',
      label: 'Tubular Segments',
      default: 100,
      panel: 'geometry'
    },
    {
      key: 'properties.arc',
      type: 'number',
      label: 'Arc',
      default: Math.PI * 2,
      panel: 'geometry'
    }
  ],
  render: (properties: TorusMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <TorusComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 